import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import getPositionAbbreviation from '../../../utils/getPositionAbbreviation';
import getPositionCapitalization from '../../../utils/getPositionCapitalization';
import getPlayerTeamCityAbbreviation from '../../../utils/getPlayerTeamCityAbbreviation';
import convertInchesToFeetAndInches from '../../../utils/convertInchesToFeetAndInches';
import PlayerComparisonModal from '../../Player/PlayerComparisonModal/PlayerComparisonModal';
import PlayerAiInsightsModal from '../../Player/PlayerAiInsightsModal/PlayerAiInsightsModal';
import StatusWidget from '../../StatusWidget/StatusWidget';
import QbTetherAlert from '../../Alerts/QbTetherAlert/QbTetherAlert';
import getWrTetheredInjuredQbAlert from '../../../utils/wrTetheredInjuredQb';
import RookieTag from '../../Tags/RookieTag';
import HofTag from '../../Tags/HofTag';
import ContractYearTag from '../../Tags/ContractYearTag';
import ChampionshipWeeksModal from '../ChampionshipWeeksModal/ChampionshipWeeksModal';
import PlayerAdpBreakdownModal from '../PlayerAdpBreakdownModal/PlayerAdpBreakdownModal';
import PricingTableModal from '../../PricingTableModal/PricingTableModal';
import NflLogo from '../../../../assets/images/National_Football_League_logo.svg';

const PlayerPortraitCard = ({
	player,
	getPlayersEndpoint,
	getPlayerAiInsightsEndpoint,
	get_prospects_endpoint,
	isProspectProfileCard,
	isDashboard,
	create_checkout_session_endpoint,
	stripe_public_key,
	stripe_product_essential_id,
	stripe_product_advanced_id,
	stripe_product_ultimate_id
}) => {
	const { subscription } = useAuth();
	const modalRef = useRef(null);

	const [
		isPlayerCompareModalOpen,
		setisPlayerCompareModalOpen
	] = useState(false);
	const [
		selectedPlayers,
		setSelectedPlayers
	] = useState([
		player
	]);
	const [
		playerAiInsights,
		setPlayerAiInsights
	] = useState(null);
	const [
		isAiInsightsModalOpen,
		setIsAiInsightsModalOpen
	] = useState(false);
	const [
		isLoadingAiInsights,
		setIsLoadingAiInsights
	] = useState(false);
	const [
		isChampionshipWeeksModalOpen,
		setIsChampionshipWeeksModalOpen
	] = useState(false);
	const [
		isPricingTableModalOpen,
		setIsPricingTableModalOpen
	] = useState(false);
	const [
		isPlayerAdpBreakdownModalOpen,
		setIsPlayerAdpBreakdownModalOpen
	] = useState(false);

	const handlePlayerComparModalClose = () => {
		setSelectedPlayers([
			player
		]);
		setisPlayerCompareModalOpen(false);
	};

	const handlePlayerSelect = (selectedPlayer) => {
		if (selectedPlayer) {
			// Check if the player is already in the selectedPlayers array
			const isAlreadySelected = selectedPlayers.some(
				(player) =>
					player.id === selectedPlayer.id &&
					player.position === selectedPlayer.position
			);

			if (!isAlreadySelected) {
				setSelectedPlayers([
					...selectedPlayers,
					selectedPlayer
				]);
			} else {
				console.warn(
					`Player ${selectedPlayer.first_name} ${selectedPlayer.last_name} is already selected.`
				);
			}
		}
	};

	const handlePlayerRemove = (playerId, playerPosition) => {
		setSelectedPlayers((prevPlayers) =>
			prevPlayers.filter(
				(player) =>
					!(player.id === playerId && player.position === playerPosition)
			)
		);
	};

	const handleCompareClick = () => {
		if (player && player.length > 0) {
			setSelectedPlayers([
				player[0]
			]);
		}
		if (
			subscription &&
			Object.keys(subscription).length > 0 &&
			subscription.status === 'active'
		) {
			setisPlayerCompareModalOpen(true);
		} else {
			setIsPricingTableModalOpen(true);
		}
	};

	const handleOpenAiInsightsModal = () => {
		if (
			subscription &&
			Object.keys(subscription).length > 0 &&
			subscription.status === 'active' &&
			(subscription.product_name === 'Advanced Fantasy Fitness' ||
				subscription.product_name === 'Ultimate Fantasy Fitness')
		) {
			fetchPlayeriInsights();
		} else {
			setIsPricingTableModalOpen(true);
		}
	};

	const handleCloseAiInsightsModal = () => {
		setIsAiInsightsModalOpen(false);
	};

	const fetchPlayeriInsights = () => {
		const endpoint = `${getPlayerAiInsightsEndpoint.replace(
			'player_id',
			player[0].id
		)}?position=${encodeURIComponent(player[0].position)}`;

		setIsLoadingAiInsights(true);
		setIsAiInsightsModalOpen(true);
		setPlayerAiInsights(null);

		fetch(endpoint)
			.then((response) => {
				if (!response.ok) throw new Error('Failed to fetch AI insights');
				return response.json();
			})
			.then((data) => {
				setPlayerAiInsights(data);
				setIsLoadingAiInsights(false);
			})
			.catch((error) => {
				console.error('Error fetching AI insights:', error);
				setIsLoadingAiInsights(false);
			});
	};

	const handleOpenChampionshipWeeksModal = () => {
		setIsChampionshipWeeksModalOpen(true);
	};

	const handleCloseChampionshipWeeksModal = () => {
		setIsChampionshipWeeksModalOpen(false);
	};

	const handleOpenPlayerAdpBreakdownModal = () => {
		setIsPlayerAdpBreakdownModalOpen(true);
	};

	const closePlayerAdpBreakdownModal = () => {
		setIsPlayerAdpBreakdownModalOpen(false);
	};

	const closePricingTableModal = () => {
		setIsPricingTableModalOpen(false);
	};

	const closePlayerAdpBreakdownModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsPlayerAdpBreakdownModalOpen(false);
		}
	};

	const closePricingTableModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsPricingTableModalOpen(false);
		}
	};

	// Prevent scrolling when modal is open
	useEffect(
		() => {
			if (isPricingTableModalOpen) {
				document.body.style.overflow = 'hidden';
			}

			return () => {
				// Cleanup on modal close or component unmount
				document.body.style.overflow = '';
			};
		},
		[
			isPricingTableModalOpen
		]
	);

	useEffect(
		() => {
			if (isPlayerAdpBreakdownModalOpen) {
				document.addEventListener(
					'mousedown',
					closePlayerAdpBreakdownModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closePlayerAdpBreakdownModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closePlayerAdpBreakdownModalOnClickOutside
				);
			};
		},
		[
			isPlayerAdpBreakdownModalOpen
		]
	);

	useEffect(
		() => {
			if (isPricingTableModalOpen) {
				document.addEventListener(
					'mousedown',
					closePricingTableModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closePricingTableModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closePricingTableModalOnClickOutside
				);
			};
		},
		[
			isPricingTableModalOpen
		]
	);

	if (player.length === 0 || !player[0].image_url) {
		return null;
	}

	const hasTeam = Boolean(player[0].team && player[0].team.id);
	const teamDisplayName = player[0].team?.name || 'Free Agent';
	const qbTetherAlert = getWrTetheredInjuredQbAlert(player);

	return (
		<div className="md:col-span-6">
			<div
				className={`grid grid-cols-1 ${isDashboard
					? ''
					: 'border border-gray-300 rounded-xl shadow-lg'} pt-2 md:pt-14 lg:pt-2 bg-white dark:bg-dark-card dark:border-dark-border text-gray-800 dark:text-dark-text`}
			>
				<div className="px-4 sm:px-6 lg:px-8 flex items-center justify-between mx-2 lg:mx-4">
					{/* Player details */}
					<div className="flex items-center flex-col lg:flex-row">
						{/* Player image */}
						<img
							className="h-48 lg:h-56 xl:h-80 rounded mb-4 sm:mb-0 lg:mr-8"
							src={player[0].image_url}
							alt="Player Portrait"
						/>
						<div className="text-center sm:text-left lg:pt-14">
							{/* Player name */}
							<div>
								<div className="flex flex-col lg:flex-row items-center mb-2 space-y-4 lg:space-y-0 lg:space-x-4">
									{isDashboard ? (
										<Link
											to={
												player[0].is_college_player ? (
													`/prospects/${player[0].id}?position=${player[0]
														.position}`
												) : (
													`/players/${player[0].id}?position=${player[0]
														.position}`
												)
											}
											className="text-3xl lg:text-4xl font-bold dark:text-dark-text mb-0 mt-4 lg:mt-0 hover:underline"
											replace
										>
											{player[0].first_name + ' ' + player[0].last_name}
										</Link>
									) : (
										<h1 className="text-3xl lg:text-4xl font-bold dark:text-dark-text mb-0 mt-4 lg:mt-0">
											{player[0].first_name + ' ' + player[0].last_name}
										</h1>
									)}
									{player[0].is_college_player ? null : ( // Don't show status for college players (no reliable data available at this time)
										<div className="flex flex-wrap items-center justify-center lg:justify-start gap-2">
											<StatusWidget
												status={player[0].status}
												description={player[0].injury_description}
											/>
											{qbTetherAlert && (
												<QbTetherAlert alert={qbTetherAlert} />
											)}
										</div>
									)}
									{isDashboard ? null : (
										<div>
											<button
												onClick={handleCompareClick}
												className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg ml-0 xl:ml-4 mb-2 xl:mb-0"
											>
												Compare
											</button>
											{player[0].is_college_player ? null : ( // Don't show status for college players (still implementing feature)
												<button
													onClick={handleOpenAiInsightsModal}
													className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg ml-4 mb-2 lg:mb-0 md:ml-4"
												>
													View AI insights
												</button>
											)}
										</div>
									)}
								</div>
							</div>
							{/* Player team, jersey number, and position */}
							<div className="flex flex-col lg:flex-row items-center mb-2">
								{/* Team image */}
								{hasTeam ? (
									<Link to={`/teams/${player[0].team_id}`} replace>
										<img
											className="h-14 sm:mr-2 mb-1 lg:mb-0"
											src={player[0].team.image_url}
											alt="Team Logo"
										/>
									</Link>
								) : (
									<img
										className="h-14 sm:mr-2 mb-1 lg:mb-0"
										src={NflLogo}
										alt="NFL Logo"
									/>
								)}
								{/* Team name, jersey number, and position */}
								<div
									className={`ext-lg font text-gray-500 dark:text-dark-text ${player[0]
										.team
										? 'ml-3'
										: ''}`}
								>
									{hasTeam ? (
										<Link
											to={`/teams/${player[0].team.id}`}
											className="text-blue-500 hover:underline"
										>
											{teamDisplayName}
										</Link>
									) : (
										<span>{teamDisplayName}</span>
									)}
									<Fragment>
										{player[0].jersey_number !== null && ` • #${player[0].jersey_number}`}
										<span className="hidden md:contents">
											{player[0].position &&
												` • ${getPositionCapitalization(player[0].position)}`}
										</span>
										<span className="contents md:hidden">
											{player[0].position &&
												` • ${getPositionAbbreviation(player[0].position)}`}
										</span>
									</Fragment>
								</div>
							</div>
							<hr className="mt-4 block lg:hidden border-gray-200 dark:border-dark-border" />

							{/* Additional attributes */}
							<div className="flex flex-wrap items-start text-base text-gray-500 dark:text-dark-text mt-6 lg:mt-2 pb-4 lg:pb-8">
								<div className="flex items-center mr-6 mb-2">
									{player[0].height &&
									player[0].weight && (
										<div>
											<span className="font-bold uppercase">HT/WT</span>
											<span className="ml-2">
												{convertInchesToFeetAndInches(player[0].height)},{' '}
												{player[0].weight} lbs
											</span>
										</div>
									)}
								</div>
								<div className="flex items-center mr-6 mb-2">
									{player[0].age && (
										<div>
											<span className="font-bold uppercase">AGE</span>
											<span className="ml-2">{player[0].age}</span>
										</div>
									)}
								</div>
								{player[0].college && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">COLLEGE</span>
										<span className="ml-2">{player[0].college}</span>
									</div>
								)}
								{!player[0].is_college_player && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">DRAFT PICK</span>
										<span className="ml-2">
											{player[0].draft_pick ? (
												`${player[0].draft_pick === '1.1'
													? '1.10'
													: player[0].draft_pick} ${player[0].draft_year
													? `(${player[0].draft_year})`
													: ''}`
											) : (
												'Undrafted'
											)}
										</span>
									</div>
								)}
								{player[0].draft_year &&
								player[0].draft_year < 2025 && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">EXP</span>
										<span className="ml-2 flex items-center">
											{`${new Date().getFullYear() - player[0].draft_year} yrs`}
											{(new Date().getFullYear() - player[0].draft_year === 0 ||
												player[0].is_rookie) && <RookieTag />}
											{player[0].is_hof && <HofTag />}
											{player[0].is_contract_year && <ContractYearTag />}
										</span>
									</div>
								)}
								{player[0].contract_details && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">CONTRACT</span>
										<a
											href={`${player[0].contract_url}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-500 hover:underline"
										>
											{' '}
											<span className="ml-2 text-blue-500 hover:underline">
												{`${player[0].contract_details} (${player[0]
													.contract_end_year} ${player[0].id === 59
													? 'RFA'
													: 'UFA'})`}
												<span className="hidden md:contents">
													<i className="fa-solid fa-arrow-up-right-from-square ml-2" />
												</span>
											</span>
										</a>
									</div>
								)}
								{player[0] &&
								player[0].underdog_adp && (
									<div
										className="flex items-center mr-6 mb-2"
										onClick={() => handleOpenPlayerAdpBreakdownModal(player[0])}
									>
										<span className="font-bold uppercase">ADP</span>
										<span className="text-blue-500 ml-2 cursor-pointer hover:underline">
											{player[0].underdog_adp}
										</span>
									</div>
								)}
								{player[0] &&
								player[0].week_15_opponent &&
								player[0].week_15_opponent.opponent_team &&
								player[0].week_15_opponent.opponent_team.location &&
								player[0].week_15_opponent.opponent_team.name &&
								player[0].week_16_opponent &&
								player[0].week_16_opponent.opponent_team &&
								player[0].week_16_opponent.opponent_team.location &&
								player[0].week_16_opponent.opponent_team.name &&
								player[0].week_17_opponent &&
								player[0].week_17_opponent.opponent_team &&
								player[0].week_17_opponent.opponent_team.location &&
								player[0].week_17_opponent.opponent_team.name && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">WKS 15, 16, 17</span>
										<button
											type="button"
											onClick={handleOpenChampionshipWeeksModal}
											className="text-blue-500 ml-2 cursor-pointer hover:underline focus:outline-none"
										>
											{getPlayerTeamCityAbbreviation(
												player[0].week_15_opponent.opponent_team.location,
												player[0].week_15_opponent.opponent_team.name
											)}
											,{' '}
											{getPlayerTeamCityAbbreviation(
												player[0].week_16_opponent.opponent_team.location,
												player[0].week_16_opponent.opponent_team.name
											)}
											,{' '}
											{getPlayerTeamCityAbbreviation(
												player[0].week_17_opponent.opponent_team.location,
												player[0].week_17_opponent.opponent_team.name
											)}
										</button>
									</div>
								)}
								{player[0] &&
								player[0].team &&
								player[0].team.bye_week && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">BYE WEEK</span>
										<span className="ml-2">{player[0].team.bye_week}</span>
									</div>
								)}
								{(player[0].twitter ||
									player[0].instagram ||
									player[0].facebook) && (
									<div className="flex items-center mr-6 mb-2">
										<span className="font-bold uppercase">SOCIAL</span>
										{player[0].twitter && (
											<a
												href={player[0].twitter}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="fab fa-x-twitter ml-2" />
											</a>
										)}
										{player[0].instagram && (
											<a
												href={player[0].instagram}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="fab fa-instagram ml-2" />
											</a>
										)}
										{player[0].facebook && (
											<a
												href={player[0].facebook}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="fab fa-facebook ml-2" />
											</a>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Player Comparison Modal */}
				<PlayerComparisonModal
					getPlayersEndpoint={getPlayersEndpoint}
					get_prospects_endpoint={get_prospects_endpoint}
					isProspectProfileCard={isProspectProfileCard}
					isPlayerCompareModalOpen={isPlayerCompareModalOpen}
					handlePlayerComparModalClose={handlePlayerComparModalClose}
					handlePlayerSelect={handlePlayerSelect}
					handlePlayerRemove={handlePlayerRemove}
					selectedPlayers={selectedPlayers}
				/>

				{/* Championship Weeks Modal */}
				<ChampionshipWeeksModal
					isOpen={isChampionshipWeeksModalOpen}
					onClose={handleCloseChampionshipWeeksModal}
					player={player[0]}
				/>

				{/* Player ADP Breakdown Modal */}
				{isPlayerAdpBreakdownModalOpen && (
					<PlayerAdpBreakdownModal
						player={player[0]}
						isPlayerAdpBreakdownModalOpen={isPlayerAdpBreakdownModalOpen}
						closePlayerAdpBreakdownModal={closePlayerAdpBreakdownModal}
						modalRef={modalRef}
					/>
				)}

				{/* Team AI Insights Modal */}
				<PlayerAiInsightsModal
					isLoading={isLoadingAiInsights}
					isOpen={isAiInsightsModalOpen}
					onClose={handleCloseAiInsightsModal}
					playerAiInsights={playerAiInsights}
				/>

				{/* Pricing Table Modal */}
				<PricingTableModal
					isOpen={isPricingTableModalOpen}
					onClose={closePricingTableModal}
					ref={modalRef}
					create_checkout_session_endpoint={create_checkout_session_endpoint}
					stripe_public_key={stripe_public_key}
					stripe_product_essential_id={stripe_product_essential_id}
					stripe_product_advanced_id={stripe_product_advanced_id}
					stripe_product_ultimate_id={stripe_product_ultimate_id}
				/>
			</div>
		</div>
	);
};

export default PlayerPortraitCard;
